import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAsync } from 'react-use'
import { AutocompleteComponent } from '@getgo/chameleon-web'
import { writeStorage } from '@rehooks/local-storage'
import debounce from 'lodash/debounce'

import { useAccountContext } from 'components/contexts/AccountContext'
import config from 'config'
import useMutationObservable from 'hooks/useMutationObservable'
import { fetchPbxs, fetchSelectedAccountDetails } from 'utils'
import { PbxType } from 'utils/constants'
import { DEFAULT_PAGINATION_SIZE, TYPING_DEBOUNCE_MILLISECONDS } from 'utils/constants'

export interface PBX {
  assignedServer: string
  domain: string
  jrn: string
  lmiAccountKey: string
  name: string
  partner: string | null
  region: string
  state: string
  type: string
  unifiedAdmin: boolean
  v5: boolean
}

export interface FetchOptionsParams {
  query: string
  offset: number
  addNewPage: boolean
}

const usePbxSwitcher = () => {
  const { setSelectedAccountKey } = useAccountContext()
  const [query, setQuery] = useState<string>('')
  const autocompleteRef = useRef<AutocompleteComponent>(null)
  const [options, setOptions] = useState<PBX[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [offset, setOffset] = useState(DEFAULT_PAGINATION_SIZE)
  const [scroll, setScroll] = useState(0)
  const [autocompleteIsOpen, setAutocompleteIsOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const mutationOptions = useMemo(
    () => ({
      config: { attributes: true, childList: false, subtree: false },
    }),
    [],
  )

  // this fetches the pbxs
  const fetchOptions = useCallback(async (params?: FetchOptionsParams) => {
    const query = params?.query || ''
    const offset = params?.offset || 0
    const addNewPage = params?.addNewPage || false
    setLoading(true)
    const result = await fetchPbxs(query, offset, [
      PbxType.CUSTOMER,
      PbxType.DEMO,
      PbxType.TEST,
      PbxType.EMPLOYEE,
      PbxType.TRAINING,
    ])
    const fetchedOptions = result.items
    setTotalCount(result.matchCount)
    if (addNewPage) {
      setOptions((value) => [...value, ...fetchedOptions])
    } else {
      setOptions(fetchedOptions)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resetSwitcher = useCallback(() => {
    if (autocompleteRef.current) {
      autocompleteRef.current.control.value = ''
      autocompleteRef.current.control.blur()
    }
    setSelectedOption('')
    setQuery('')
    setScroll(0)
    setOffset(DEFAULT_PAGINATION_SIZE)
    fetchOptions({ query: '', offset: 0, addNewPage: false })
  }, [fetchOptions])

  useAsync(async () => {
    if (selectedOption) {
      const newAccountDetails = await fetchSelectedAccountDetails(selectedOption)
      if (newAccountDetails) {
        writeStorage(config.SELECTED_ACCOUNT_KEY, { key: newAccountDetails.key })
        writeStorage(config.SELECTED_PBX_ID, newAccountDetails.orgId)
        setSelectedAccountKey && setSelectedAccountKey(newAccountDetails.key)
        resetSwitcher()
      }
    }
  }, [selectedOption])

  // this set the scroll bar to where it was before (bottom)
  useEffect(() => {
    if (autocompleteRef.current) {
      autocompleteRef.current.listbox.scrollTop = scroll
    }
  }, [scroll])

  useEffect(() => {
    if (autocompleteIsOpen && autocompleteRef.current) {
      setScroll(0)
      autocompleteRef.current.listbox.scrollTop = 0
    }
  }, [autocompleteIsOpen])

  const onAutocompleteListboxMutation = useCallback((mutationsList) => {
    const listboxMutation = mutationsList.find(
      (mutation) =>
        mutation.type === 'attributes' &&
        mutation.attributeName === 'class' &&
        mutation.target.getAttribute('part') === 'listbox',
    )
    if (listboxMutation) {
      setAutocompleteIsOpen((prev) => !prev)
    }
  }, [])

  useMutationObservable(autocompleteRef.current?.listbox, onAutocompleteListboxMutation, mutationOptions)

  // this handles the scroll event
  const handleScroll = useCallback(async () => {
    const listBox = autocompleteRef.current?.listbox

    if (!listBox) return

    const { scrollHeight, scrollTop, clientHeight } = listBox
    if (scrollHeight - scrollTop === clientHeight && options.length < totalCount) {
      const newOffset = offset + DEFAULT_PAGINATION_SIZE
      setScroll(scrollTop)
      setOffset(newOffset)
      await fetchOptions({ query, offset: newOffset, addNewPage: true })
    }
  }, [fetchOptions, offset, query, options.length, totalCount])

  useEffect(() => {
    const listBox = autocompleteRef.current?.listbox
    if (!listBox) return
    listBox.addEventListener('scroll', handleScroll)
    return () => {
      listBox.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  const handlerOnChange = useCallback((e) => {
    const value = e.detail?.selectedOptions?.[0]?.value || ''
    setSelectedOption(value)
  }, [])

  // this is the search debounce
  const debouncedSearch = useRef(
    debounce((value) => {
      setQuery(value)
      setScroll(0)
      setOffset(0)
      fetchOptions({ query: value, offset: 0, addNewPage: false })
    }, TYPING_DEBOUNCE_MILLISECONDS),
  ).current

  const handlerOnInput = useCallback(
    (e: CustomEvent<{ value: string }>) => {
      debouncedSearch(e.detail.value)
    },
    [debouncedSearch],
  )

  const handlerOnClear = useCallback(() => setSelectedOption(''), [])

  return {
    autocompleteRef,
    loading,
    options,
    scroll,
    selectedOption,
    handlerOnClear,
    handlerOnChange,
    handlerOnInput,
  }
}

export default usePbxSwitcher
